/**
 * @fileoverview
 * FAQ Section for the homepage.
 *
 * Integrates the existing FAQAccordion component with the faqData
 * to display the Frequently Asked Questions section with anchor #faq.
 *
 * This is a thin wrapper around the existing FAQAccordion component,
 * maintaining visual parity with live aideco.us.
 */

"use client";

import { FAQAccordion } from "@/components/marketing/faq-accordion";
import { featureFlags } from "@/config/features";
import { useLingui } from "@lingui/react/macro";

/**
 * FAQSection renders the Frequently Asked Questions section.
 *
 * Uses the existing FAQAccordion component for expand/collapse functionality
 * and the faqData for content. Includes anchor id="faq" for navigation.
 *
 * @returns JSX.Element
 */
export function FAQSection() {
  const { t } = useLingui();
  const faqs = [
    {
      question: t`What is Aideco?`,
      answer:
        t`Aideco is an AI-powered architecture rendering platform that transforms CAD drawings, floor plans, and sketches into photorealistic, 3D, clay, or sketch-style renders in minutes. We help architecture teams present concepts faster and more beautifully.`,
    },
    {
      question: t`How does AI image generation work?`,
      answer:
        t`Our AI analyzes your uploaded architectural drawings or images and applies advanced machine learning models trained on architectural rendering techniques. You select a style (photorealistic, clay, sketch, etc.) and mode (exterior, interior, floor plan), and our AI generates high-quality renders that match your specifications.`,
    },
    {
      question: t`What file formats are supported?`,
      answer:
        t`We support common image formats including JPG, PNG, and WebP. You can upload AutoCAD drawings, SketchUp exports, Revit screenshots, hand-drawn sketches, or any architectural visualization in these formats. Files up to 10MB are supported on most plans.`,
    },
    {
      question: t`How many credits do generations cost?`,
      answer: featureFlags.enablePaidGate
        ? t`Each render generation costs 1 credit. The Starter plan includes 25/day, Pro includes 100/day, and Enterprise includes 500/day. Daily limits reset at midnight UTC. New accounts start with a Stripe-managed 3-day free trial.`
        : t`Each render generation costs 1 credit. Free users get 3 generations per day. The Starter plan includes 25/day, Pro includes 100/day, and Enterprise includes 500/day. Daily limits reset at midnight UTC.`,
    },
    {
      question: t`Can I use generated images commercially?`,
      answer: featureFlags.enablePaidGate
        ? t`Yes! All renders you generate on any plan (Starter, Pro, Enterprise) are yours to use commercially without restrictions. You can use them in client presentations, marketing materials, competitions, and portfolio work.`
        : t`Yes! All renders you generate on paid plans (Starter, Pro, Enterprise) are yours to use commercially without restrictions. You can use them in client presentations, marketing materials, competitions, and portfolio work. Free plan users have personal use rights only.`,
    },
    {
      question: t`What's the difference between exterior, interior, and floor plan modes?`,
      answer:
        t`Exterior mode optimizes for building facades, landscaping, and outdoor spaces. Interior mode focuses on room layouts, furniture, lighting, and material textures. Floor plan mode converts 2D layouts into 3D visualizations or enhanced plan views. Each mode uses specialized AI models trained for that specific rendering type.`,
    },
    {
      question: t`How long does it take to generate a render?`,
      answer:
        t`Most renders complete in 30-90 seconds depending on complexity and current server load. You can queue multiple generations and continue working while they process. Pro and Enterprise users get priority processing for faster turnaround times.`,
    },
  ] as const;

  return (
    <FAQAccordion
      faqs={faqs}
      subtitle={t`FAQ`}
      title={t`Frequently Asked Questions`}
    />
  );
}
